import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { IconEye, IconEyeOff } from '@tabler/icons-react';
import type { ShelfItem } from '@/api/types';
import { haptic } from '@/lib/telegram';
import { OpenBookConfirm } from '@/features/shelf/OpenBookConfirm';

interface Props {
  item: ShelfItem;
  onHide: (cardId: string, hide: boolean) => void;
  onDelete: (cardId: string) => void;
}

export function CompactQuoteRow({ item, onHide, onDelete }: Props) {
  const { t } = useTranslation();
  const nav = useNavigate();
  const [confirmOpen, setConfirmOpen] = useState(false);
  return (
    <div
      role="button"
      tabIndex={0}
      onClick={() => { haptic.light(); setConfirmOpen(true); }}
      className="flex items-start gap-3 px-4 py-3 border-t border-text/5 cursor-pointer"
    >
      <p className="font-content text-sm leading-snug text-text flex-1">{item.text ?? '—'}</p>
      <div className="flex items-center gap-1.5 shrink-0 pt-0.5">
        {item.hidden_from_public && (
          <span className="text-[10px] uppercase tracking-wider text-text-muted">{t('shelf.hidden_label')}</span>
        )}
        <button
          aria-label={item.hidden_from_public ? t('common.show') : t('shelf.hide_for_all')}
          onClick={(e) => { e.stopPropagation(); onHide(item.id, !item.hidden_from_public); }}
          className="p-1.5 rounded-lg text-text-muted hover:text-warning transition-colors"
        >
          {item.hidden_from_public ? <IconEye size={15} /> : <IconEyeOff size={15} />}
        </button>
        <button
          aria-label={t('shelf.delete')}
          onClick={(e) => { e.stopPropagation(); onDelete(item.id); }}
          className="p-1.5 rounded-lg text-text-muted hover:text-danger transition-colors text-sm leading-none"
        >
          ✕
        </button>
      </div>
      <OpenBookConfirm
        open={confirmOpen}
        bookTitle={item.book_title}
        bookAuthor={item.book_author}
        onCancel={() => setConfirmOpen(false)}
        onConfirm={() => {
          setConfirmOpen(false);
          nav(`/book/${item.book_id}`, { state: { from: '/shelf' } });
        }}
      />
    </div>
  );
}
